import MarkdownIt from 'markdown-it'
import type { MarkdownIt as MarkdownItInstance, StateCore, Token } from 'markdown-it'
import anchor from 'markdown-it-anchor'

// Slug for heading anchor ids: lowercase, non-word runs → single
// hyphen, trimmed. Keeps deep-link fragments stable + ToC-ready.
function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replaceAll(/[^\w]+/g, '-')
    .replaceAll(/^-+|-+$/g, '')
}
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useWikiPagesStore } from '../stores/wiki_pages'
import { classifyLink } from './useWikiMarkdown.linkClassifier'
import { installProvenanceRule } from './useWikiMarkdown.provenance'

// Re-exported for the existing test surface
// (`import { resolveWikiLink } from './useWikiMarkdown'`) and any view
// that classifies a link without going through markdown-it.
export { resolveWikiLink } from './useWikiMarkdown.linkClassifier'

// Bare wiki paths that should auto-link in prose. Captures things like
// `entities/foo.md`, `concepts/bar-baz.md`, and bare `log.md`. Requires
// at least one word character so we don't match leading punctuation; the
// word-boundary lookbehind avoids snatching the right-hand side of
// `http://example.md` or `some@thing.md`.
const WIKI_PATH_RE = /(?<![\w@:/])[\w][\w./-]*\.md\b/g

export interface WikiRenderEnv {
  currentPath?: string
  // Catalog of wiki-relative paths. When present, lets the renderer
  // rewrite bare filenames (e.g. `anthropic.md`) to their actual home
  // (e.g. `concepts/anthropic.md`) instead of dead-linking to root.
  knownPaths?: readonly string[]
}

/**
 * Builds a MarkdownIt instance configured for wiki rendering. Three
 * anchor shapes get distinct treatments:
 *
 *   - Internal `.md` link  — href rewritten to `/p/<path>`; stamped with
 *                            `data-wiki-link="true"` so the click
 *                            interceptor in HomeView can route via
 *                            `router.push` instead of a full page reload.
 *   - External URL         — `target="_blank"` + `rel="noopener noreferrer"`
 *                            added so the link opens in a new tab without
 *                            losing the user's SPA state (log.md entries
 *                            like `[fpl-stats](http://github.com/…)` would
 *                            otherwise navigate away and lose session).
 *   - Everything else      — left alone. In-page anchors scroll, relative
 *                            non-markdown paths are passed through for
 *                            whatever the browser decides to do.
 *
 * Callers pass the current page's wiki-relative path through the render
 * env: `md.render(content, { currentPath: 'concepts/foo.md' })`.
 */
export function createWikiMarkdown(): MarkdownItInstance {
  const md = new MarkdownIt({ html: false, linkify: true, breaks: false })
  // linkify-it auto-matches bare tokens that look like domains/emails/IPs.
  // `.md` is a real ccTLD (Moldova), so without these flags a log entry
  // like "updated index.md" becomes `<a href="http://index.md/">index.md</a>`
  // — pointing at an unrelated public domain. Callers who actually mean a
  // link should use explicit markdown (`[text](url)`) or include the scheme.
  md.linkify.set({ fuzzyLink: false, fuzzyEmail: false, fuzzyIP: false })

  // Auto-link bare `.md` paths inside prose so log.md lines like
  // `2026-04-17 — Ingested entities/foo.md` become clickable without
  // requiring the writer to emit explicit markdown link syntax. Runs
  // AFTER the inline rule so already-linked paths (inside `[...]()`)
  // and `code_inline` tokens stay untouched.
  md.core.ruler.after('inline', 'wikiPathAutolink', autolinkWikiPaths)

  // Registered after the autolinker, so `after('inline')` places it first:
  // provenance comments are gone before `.md` paths are scanned.
  installProvenanceRule(md)

  // Stamp `id` on h1–h3 so a long page's sections are deep-linkable and a
  // future table-of-contents has anchors to target. No permalink
  // glyph — ids only, to keep the rendered prose clean.
  md.use(anchor, { level: [1, 2, 3], slugify: slugifyHeading })

  const defaultRender =
    md.renderer.rules.link_open || ((tokens, idx, options, _env, self) => self.renderToken(tokens, idx, options))

  md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
    const token = tokens[idx]
    const hrefIdx = token.attrIndex('href')
    if (hrefIdx >= 0 && token.attrs) {
      const href = String(token.attrs[hrefIdx][1])
      const wikiEnv = env as WikiRenderEnv | undefined
      const currentPath = wikiEnv?.currentPath ?? ''
      const classification = classifyLink(href, currentPath, wikiEnv?.knownPaths)

      if (classification.kind === 'wiki') {
        token.attrs[hrefIdx][1] = classification.href
        token.attrSet('data-wiki-link', 'true')
      } else if (classification.kind === 'external') {
        // noopener cuts off window.opener access; noreferrer additionally
        // strips the Referer header (privacy). Both are safe defaults.
        token.attrSet('target', '_blank')
        token.attrSet('rel', 'noopener noreferrer')
      }
    }
    return defaultRender(tokens, idx, options, env, self)
  }

  return md
}

function autolinkWikiPaths(state: StateCore): boolean {
  for (const blockToken of state.tokens) {
    if (blockToken.type !== 'inline' || !blockToken.children) continue
    const next: Token[] = []
    let linkDepth = 0
    for (const token of blockToken.children) {
      if (token.type === 'link_open') linkDepth++
      if (linkDepth > 0 || token.type !== 'text') {
        next.push(token)
        if (token.type === 'link_close') linkDepth--
        continue
      }
      splitTextForWikiPaths(token, next, state)
    }
    blockToken.children = next
  }
  return false
}

function splitTextForWikiPaths(token: Token, out: Token[], state: StateCore): void {
  const text = token.content
  WIKI_PATH_RE.lastIndex = 0
  let cursor = 0
  let match: RegExpExecArray | null
  let found = false
  while ((match = WIKI_PATH_RE.exec(text)) !== null) {
    found = true
    if (match.index > cursor) {
      const leading = new state.Token('text', '', 0)
      leading.content = text.slice(cursor, match.index)
      out.push(leading)
    }
    const open = new state.Token('link_open', 'a', 1)
    open.attrs = [['href', match[0]]]
    out.push(open)
    const label = new state.Token('text', '', 0)
    label.content = match[0]
    out.push(label)
    out.push(new state.Token('link_close', 'a', -1))
    cursor = match.index + match[0].length
  }
  if (!found) {
    out.push(token)
    return
  }
  if (cursor < text.length) {
    const trailing = new state.Token('text', '', 0)
    trailing.content = text.slice(cursor)
    out.push(trailing)
  }
}

// Re-export the citation surface so existing callers
// (`import { CitationRef, CITATION_MARKER_CLASS } from './useWikiMarkdown'`)
// keep working — the implementation lives in the sibling module to
// keep this file focused on link classification + markdown-it setup.
export { CITATION_MARKER_CLASS, type CitationRef } from './useWikiMarkdown.citations'

import { injectInlineCitations, type CitationRef as _CitationRef } from './useWikiMarkdown.citations'

export interface WikiMarkdown {
  render: (content: string, currentPath?: string) => string
  // Renders `content` then inserts numbered `[n]` superscript markers
  // after the FIRST occurrence of each cited path in the body. Paths
  // that are listed in `citations` but never mentioned in the answer
  // text leave no marker — the parent component still shows a footer
  // "Sources" list to surface those.
  renderWithCitations: (content: string, currentPath: string | undefined, citations: readonly _CitationRef[]) => string
}

/**
 * Vue composable that hands callers a render function with the wiki
 * catalog already bound from the Pinia store. Views call
 * `wikiMarkdown.render(content, currentPath)` and never have to know
 * about `knownPaths` — that's the deep-module boundary.
 *
 * `knownPathsOverride` exists for tests and isolated views that don't
 * want the global store (the standard `setActivePinia` setup in unit
 * tests would otherwise force every consumer's spec to wire one up).
 */
export function useWikiMarkdown(knownPathsOverride?: () => readonly string[]): WikiMarkdown {
  const md = createWikiMarkdown()
  const knownPathsFn = knownPathsOverride ?? defaultKnownPathsFromStore()
  function render(content: string, currentPath?: string): string {
    return md.render(content || '', {
      currentPath,
      knownPaths: knownPathsFn(),
    })
  }
  return {
    render,
    renderWithCitations(content, currentPath, citations) {
      const html = render(content, currentPath)
      return injectInlineCitations(html, citations, currentPath, knownPathsFn())
    },
  }
}

function defaultKnownPathsFromStore(): () => readonly string[] {
  const { pages } = storeToRefs(useWikiPagesStore())
  const paths = computed(() => pages.value.map((p) => p.path))
  return () => paths.value
}
